import {Component, HostListener, Inject, Input, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {environment} from "@environment";
import {DOCUMENT} from '@angular/common';
import {TranslocoService} from '@ngneat/transloco';
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {AbstractComponent, ConfirmationModalComponent, ValidationModalComponent} from "@components";
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
  EventFilter,
  EventItem, EventStatus,
  FindEventsRequest,
  Lookups,
  Organiser, Paging,
  Race, Request
} from "@model";
import {
  AuthenticationService,
  EventService,
  ExportService, LoaderService,
  RequestService, TOAST_STATE,
  ToastService
} from "@services";
import {
  ORGANISER_EVENT_ADD, ORGANISER_EVENT_COPY,
  ORGANISER_EVENT_EDIT, ORGANISER_EVENT_PREVIEW,
  ORGANISER_EVENTS_MANAGEMENT
} from "src/app/app-routing.module";
import {debounceTime, distinctUntilChanged} from 'rxjs';
import {ValidatorResult} from "../../../../../model/validation/ValidationError";

@Component({
  selector: 'app-list-of-events',
  templateUrl: './list-of-events.component.html',
  styleUrls: ['./list-of-events.component.scss'],
})
export class ListOfEventsComponent extends AbstractComponent implements OnInit, Paging<EventItem> {
  @Input('eventStatuses') statuses: Lookups[] = []
  @Input({required: true}) languages: Lookups[] = [];
  @Input() organisers: Organiser[] = [];
  @Input() events: EventItem[] = [];

  filter: EventFilter = new EventFilter();

  races: Race[] = [];
  name: string = "";
  dateTo: Date | undefined;
  dateFrom: Date | undefined;
  page: number = 0;
  pageExport: number = 0;
  showFabButton: boolean = false;
  environment: any = environment;
  statusId: number | undefined;
  organiserId: number | undefined;
  hasMoreEvents: boolean = false;
  isLoading: boolean = false;
  queryParamsFirstChange: boolean = true;

  constructor(
    private router: Router,
    private exportService: ExportService,
    private route: ActivatedRoute,
    private requestService: RequestService,
    public authService: AuthenticationService,
    private activatedRoute: ActivatedRoute,
    private toastService: ToastService,
    private eventService: EventService,
    @Inject(DOCUMENT) private document: Document,
    public translocoService: TranslocoService,
    private loaderService: LoaderService,
    private modalService: NgbModal,
  ) {
    super();
  }

  ngOnInit() {
    this.eventService.setCurrentEvent(undefined);
    this.handleExploreResult(this.events);
    this.subscribeToRouteDataChange();
  }

  subscribeToRouteDataChange() {
    this.subscriptions.push(this.activatedRoute.queryParams.pipe(debounceTime(environment.fullTextSearchDelay),
        distinctUntilChanged()).subscribe((queryParams: any) => {
        this.name = queryParams.name ? queryParams.name : "";
        this.organiserId = this.getOrganiserValue(queryParams);
        this.statusId = this.getStatusValue(queryParams);
        if (!this.queryParamsFirstChange && this.isStatusValid(queryParams.status) && this.isOrganiserValid(queryParams.organiser)) {
          this.onExplore(false);
        }
        this.queryParamsFirstChange = false;
      })
    );
  }

  private changeQueryParams() {
    let queryParams: { [key: string]: any } = {};
    if (this.statusId !== undefined && this.statusId !== null) {
      queryParams['status'] = this.statusId;
    }
    if (this.organiserId) {
      queryParams['organiser'] = this.organiserId;
    }
    if (this.name && this.name.length > 0) {
      queryParams['name'] = this.name;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true,
    });
  }


  getFileFromServer(type: string) {
    this.exportService.downloadListOfEventsFile(type, this.createFilterExport()).subscribe(
      (blob: Blob) => {
        const formattedDateTime: string = this.getCurrentDateTimeFormatted();
        const filename: string = type + '_' + formattedDateTime + '.' + type;
        const downloadLink: HTMLAnchorElement = this.document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(blob);
        downloadLink.setAttribute('download', filename);
        this.document.body.appendChild(downloadLink);
        downloadLink.click();
      },
      error => {
        console.error('Error downloading file:', error);
      }
    );
  }

  getCurrentDateTimeFormatted(): string {
    const currentDateTime = new Date();
    const year: number = currentDateTime.getFullYear();
    const month: string = this.padNumber(currentDateTime.getMonth() + 1, 2);
    const day: string = this.padNumber(currentDateTime.getDate(), 2);
    return `${year}-${month}-${day}`;
  }

  padNumber(num: number, size: number): string {
    let numStr = num.toString();
    while (numStr.length < size) {
      numStr = '0' + numStr;
    }
    return numStr;
  }

  @HostListener('window:scroll')
  toggleFabButton(): void {
    this.showFabButton = window.pageYOffset != 0;
  }

  scrollToTop(): void {
    return this.document.body.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'start'
    });
  }

  trackByFn(index: number, item: EventItem): number | undefined {
    return item.id;
  }

  createFilter() {
    return FindEventsRequest.createEventPageFilter(
      this.page,
      {
        name: this.name !== "" ? this.name : undefined,
        dateFrom: undefined,
        dateTo: undefined,
        status: this.statusId ?? undefined,
        sport: undefined,
        selectedMonth: undefined,
        length: undefined,
        organiserId: this.organiserId ?? undefined,
      });
  }

  createFilterExport() {
    return FindEventsRequest.createEventPageFilter(
      this.pageExport++,
      {
        name: this.name !== "" ? this.name : undefined,
        dateFrom: undefined,
        dateTo: undefined,
        status: this.statusId ?? undefined,
        sport: undefined,
        selectedMonth: undefined,
        length: undefined,
        organiserId: this.organiserId ?? undefined,
      });
  }

  private getStatusValue(queryParams: any) {
    if (queryParams.status) {
      return this.isStatusValid(queryParams.status) ? +queryParams.status : undefined
    }
    return undefined;
  }

  private isStatusValid(statusId: string | undefined) {
    if (!statusId) return true;
    else {
      return this.statuses.filter((status: Lookups) => status.id === +statusId).length > 0 ?? false;
    }
  }

  private getOrganiserValue(queryParams: any) {
    if (queryParams.organiser) {
      return this.isOrganiserValid(queryParams.organiser) ? +queryParams.organiser : undefined
    }
    return undefined;
  }

  private isOrganiserValid(organiserId: string | undefined) {
    if (!organiserId) return true;
    else {
      return this.organisers.filter((organiser: Organiser) => organiser.id === +organiserId).length > 0 ?? false;
    }
  }

  handleExploreResult(value: EventItem[], loadMore?: boolean): void {
    if (value) {
      if (value.length > DEFAULT_PAGE_SIZE) {
        value = (value.slice(0, DEFAULT_PAGE_SIZE))
        this.hasMoreEvents = true;
      } else {
        this.hasMoreEvents = false;
      }
      if (loadMore && this.events) {
        value = this.events.concat(value)
      }
    }
    this.setListOfItems(value);
  }

  setListOfItems(value: EventItem[] | null) {
    if (value && value.length > 0) {
      this.events = value;
      this.page++;
    } else {
      this.events = [];
    }
  }

  onExplore(loadMore?: boolean): void {
    if (loadMore === true) {
      this.loaderService.startLoading();
    } else {
      this.page = 1;
      this.isLoading = true;
    }
    this.eventService.fetchAdminEvents(this.createFilter()).subscribe((value: EventItem[]) => {
        if (loadMore === true) {
          this.loaderService.dismissLoading();
        } else {
          setTimeout(() => {
            this.isLoading = loadMore ? this.isLoading : false;
          }, 300);
        }
        this.handleExploreResult(value, loadMore);
      }
    )
  }

  loadMore() : void {
    this.onExplore(true);
  }

  deleteAvailable(event: EventItem): boolean {
    return (this.authService.isOrganiser() && event.status === EventStatus.INACTIVE) || this.authService.isAdmin();
  }

  openPublishEventAdminDialog(event: EventItem) {
    const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("event.item.publish");
    modalRef.componentInstance.text = this.translocoService.translate("event.item.publish.dialog.text");
    modalRef.result.then((_) => {
      this.publishEvent(event);
    }, (_) => {
      /// ignore
    });
  }

  publishEvent(event: EventItem) {
    let eventDetails: EventItem = {...event}
    eventDetails.status = EventStatus.ACTIVE;
    this.eventService.updateEvent(eventDetails, () => {
      this.toastService.showToast(TOAST_STATE.success,
        this.translocoService.translate("event.item.list.publish.success"));
      event.status = EventStatus.ACTIVE;
    }, () => {
      this.toastService.showToast(TOAST_STATE.danger,
        this.translocoService.translate("event.item.list.publish.error"))
    }).subscribe((_) => {

    });
  }

  sendPublishRequest(event: EventItem) {
    this.requestService.sendPublishEventRequest(Request.createPublishEventRequest(event), () => {
      event.status = EventStatus.PUBLISHING;
      this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("request.sending.success"))
    }, () => {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.common.error"))
    }).subscribe((val: ValidatorResult | null) => {
      if (val) {
        const modalRef: NgbModalRef = this.modalService.open(ValidationModalComponent, {
          centered: true,
          size: 'lg'
        });

        modalRef.componentInstance.title = this.translocoService.translate("app.validation.error.event");
        modalRef.componentInstance.validatorResult = val
        ;
        modalRef.result.then((_) => {

        }, (_) => {
          /// ignore
        });
      }
    });
  }

  onPublish(event: EventItem) {
    if (!this.authService.isAdmin()) {
      this.sendPublishRequest(event);
    } else {
      this.openPublishEventAdminDialog(event);
    }
  }

  shouldShowPublishButton(event: EventItem) {
    return (event.status === EventStatus.INACTIVE)
  }

  onStatusChange(statusId: number | undefined) {
    this.statusId = statusId
    this.changeQueryParams()
  }

  onOrganiserChange(organiserId: number | undefined) {
    this.organiserId = organiserId;
    this.changeQueryParams()
  }

  handleSearchInput(value: string | Event): void {
    let searchString: string;

    if (typeof value === 'string') {
      searchString = value;
    } else {
      searchString = (value.target as HTMLInputElement).value;
    }

    searchString = searchString.trim();
    this.name = searchString.length > 0 ? searchString : "";
    this.changeQueryParams();
  }

  goToOrganiserEventDetails(eventItem: EventItem): void {
    if (eventItem && eventItem.id) {
      this.eventService.setCurrentEvent(eventItem);
      this.router.navigate([ORGANISER_EVENT_EDIT], {queryParams: {eventId: eventItem.id}});
    }
  }

  goToEventManagement(eventItem: EventItem): void {
    if (eventItem && eventItem.id) {
      this.eventService.setCurrentEvent(eventItem);
      this.router.navigate([ORGANISER_EVENTS_MANAGEMENT], {queryParams: {eventId: eventItem.id}});
    }
  }

  goToAddEvent(): void {
    this.router.navigate([ORGANISER_EVENT_ADD]);
  }

  goToCopyEvent(eventItem: EventItem): void {
    if (eventItem && eventItem.id) {
      this.eventService.setCurrentEvent(eventItem);
      this.router.navigate([ORGANISER_EVENT_COPY], {queryParams: {eventId: eventItem.id}});
    }
  }

  openEraseEventConfirmationDialog(event: EventItem): void {
    const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });

    modalRef.componentInstance.text = this.translocoService.translate("event.item.list.delete.modal.text");
    modalRef.componentInstance.title = this.translocoService.translate("event.item.list.delete.modal.title");
    modalRef.result.then((_) => {
      this.eventService.deleteEvent(event, () => {
        this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("event.item.list.delete.success"));
        this.page = DEFAULT_PAGE_INDEX;
        this.loaderService.startLoading();
        return this.eventService.fetchAdminEvents(FindEventsRequest.createEventPageFilter(DEFAULT_PAGE_INDEX)).subscribe((value: EventItem[]) => {
          this.loaderService.dismissLoading();
          this.handleExploreResult(value, false);
        });
      }, () => {
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("event.item.list.delete.error"));
      });
    }, (_) => {
      /// ignore
    });
  }

  goToEventPreview(eventItem:EventItem): void {
    if (eventItem?.id) {
      this.eventService.setCurrentEvent(eventItem);
      this.router.navigate([ORGANISER_EVENT_PREVIEW], {queryParams: {eventId: eventItem.id}});
    }
  }
}
